import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Brand, Sidebar, SidebarNav } from '@/app/layout/Sidebar';
import { GlobalSearch } from '@/app/layout/GlobalSearch';
import { ThemeToggle } from '@/app/theme/ThemeToggle';
import { UserMenu } from '@/app/layout/UserMenu';
import { NAV_ITEMS } from '@/app/layout/nav';

function useSectionTitle(): string {
  const { pathname } = useLocation();
  const item = NAV_ITEMS.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
  return item?.label ?? 'ClawHire';
}

/**
 * Application frame: desktop sidebar, mobile drawer, top bar (search, theme, user) and the page
 * outlet. Pages render inside `<main>`; use `PageHeader` as their first element.
 */
export function AppShell() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const section = useSectionTitle();

  return (
    <div className="flex min-h-screen w-full">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:rounded-md focus:bg-background focus:px-3 focus:py-2 focus:shadow"
      >
        Skip to content
      </a>
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <Menu aria-hidden="true" />
            </Button>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SheetDescription className="sr-only">Main sections of ClawHire</SheetDescription>
              <div className="flex h-14 items-center border-b px-3">
                <Brand />
              </div>
              <div className="p-3">
                <SidebarNav onNavigate={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
          <h2 className="mr-auto truncate text-sm font-semibold md:text-base">{section}</h2>
          <GlobalSearch />
          <ThemeToggle />
          <UserMenu />
        </header>
        <main id="main" className="flex-1 px-4 py-5 sm:px-6 lg:px-8" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
